/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    Component,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    IAZCluster,
    INsxtCluster,
} from 'generated-types';

import { AZCluster } from 'object-types';

import {
    HttpMethod,
    HttpWrapper,
    HTTP_WRAPPER_TOKEN,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import * as l10n from './az-cluster.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * NSX Clusters API URL.
 */
const NSX_CLUSTER_API_URL = 'api/nsxt/clusters';

/**
 * @description
 *
 *   Az Cluster Message Item.
 *
 * @author Sarthak kapoor, Umesh Chovatiya
 */
export class AZClusterConfigItem extends withFullModalMixin(MessageItem)<IAZCluster> {
    protected static ngDependencies = [
        'l10nService',
        HTTP_WRAPPER_TOKEN,
    ];

    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private httpWrapper: HttpWrapper;

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: AZCluster,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getNgDependency(HTTP_WRAPPER_TOKEN);

        this.httpWrapper = new HttpWrapper();

        this.l10nService = this.getNgDependency('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            AZClusterModalComponent,
        } = await import(
            /* webpackChunkName: "availability-zone-modal" */
            /* eslint-disable-next-line max-len */
            'ng/lazy-loaded-components/modals/availability-zone-modal/az-cluster-modal/az-cluster-modal.component'
        );

        return AZClusterModalComponent as Type<Component>;
    }

    /**
     * Fetch vcenter cloud Data Store list.
     */
    public async fetchNsxtClustersList(
        cloudId: string,
        vcenterId: string,
        isInvokedFromParentModal = false,
    ): Promise<INsxtCluster[]> {
        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: NSX_CLUSTER_API_URL,
                method: HttpMethod.POST,
                data: {
                    cloud_uuid: cloudId,
                    vcenter_uuid: vcenterId,
                },
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { nsxt_clusters: nsxtClusters = [] } = resource;

            return nsxtClusters;
        } catch (error) {
            if (isInvokedFromParentModal) {
                throw error;
            }

            this.errors = error.data;
        } finally {
            this.busy = false;
        }
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.azClusterBreadcrumbTitle);
    }
}
